<template>
  <div class="content bg8">
    <div class="advising-shell">
      <section class="advising-hero">
        <div class="advising-hero-copy">
          <div class="eyebrow">Founder Advising</div>
          <h1>Support for founders who need sharper focus, better decisions, and more traction.</h1>
          <p class="hero-lead">
            Founder advising with Foclarity is built for the moments when there is too much on your plate,
            too much uncertainty, and too much important work competing for attention.
          </p>
          <p class="hero-sub">
            We help founders cut through noise, clarify what matters most, and build a healthier rhythm for
            priorities, ownership, and execution.
          </p>
          <div class="hero-actions">
            <a href="https://www.calendly.com/foclarity/30min" target="_blank" rel="nofollow" class="cta-link">
              <button class="cta-btn primary">Book a Conversation</button>
            </a>
            <router-link to="/contact" class="cta-link secondary-link">
              <button class="cta-btn secondary">Send a Message</button>
            </router-link>
          </div>
        </div>
        <div class="advising-hero-panel">
          <div class="signal-card">
            <div class="signal-title">This is especially useful when you are:</div>
            <ul>
              <li>trying to make high-stakes tradeoffs with limited time</li>
              <li>feeling scattered across too many priorities</li>
              <li>growing faster than your current systems can support</li>
              <li>leading alone and wanting a better thought partner</li>
            </ul>
          </div>
        </div>
      </section>

      <section class="advising-intro">
        <div class="section-head">
          <div class="eyebrow">What Advising Looks Like</div>
          <h2>Practical, founder-centered support that helps you move forward with more intention.</h2>
          <p>
            This is not generic coaching and not abstract strategy work. It is a structured way to think more clearly,
            focus the business on what matters now, and create more follow-through around the priorities that drive growth.
          </p>
        </div>
        <div class="offer-grid">
          <article class="offer-card">
            <h3>Clarify priorities</h3>
            <p>
              Get clearer on what matters most right now, what can wait, and what is distracting the business from real progress.
            </p>
          </article>
          <article class="offer-card">
            <h3>Make better tradeoffs</h3>
            <p>
              Work through strategic choices, competing opportunities, and operational bottlenecks with a more grounded decision process.
            </p>
          </article>
          <article class="offer-card">
            <h3>Build stronger execution</h3>
            <p>
              Turn priorities into a visible system with scorecards, ownership, tasks, and better operating rhythm.
            </p>
          </article>
          <article class="offer-card">
            <h3>Stay aligned as you grow</h3>
            <p>
              Create more clarity for yourself and your team so progress does not depend on constant firefighting.
            </p>
          </article>
        </div>
      </section>

      <section class="fit-section">
        <div class="fit-panel copy">
          <div class="eyebrow">Best Fit</div>
          <h2>Best for early founders, solopreneurs, and owner-operators building with intention.</h2>
          <p>
            Founder advising tends to be most valuable when you are carrying a lot, moving quickly, and trying to grow
            without losing clarity, confidence, or momentum.
          </p>
        </div>
        <div class="fit-panel list">
          <div class="fit-item">Early founders navigating uncertainty and tradeoffs</div>
          <div class="fit-item">Bootstrapped businesses trying to grow without chaos</div>
          <div class="fit-item">Solopreneurs who need more structure and follow-through</div>
          <div class="fit-item">Small teams that need clearer priorities and better alignment</div>
        </div>
      </section>

      <section class="founder-section">
        <div class="section-head compact">
          <div class="eyebrow">About Mash</div>
          <h2>Advising grounded in real startup experience.</h2>
        </div>
        <div class="founder-grid">
          <div class="founder-photo-card">
            <img src="@/assets/mash-michael-ashley.png" alt="Mash Michael Ashley" class="founder-photo" />
            <div class="founder-links">
              <a href="https://www.linkedin.com/in/mashley" target="_blank" rel="nofollow">LinkedIn</a>
              <router-link to="/about">Read the full story</router-link>
            </div>
          </div>
          <div class="founder-copy">
            <h3>Mash (Michael Ashley), Founder and CEO</h3>
            <p>
              Mash brings more than 25 years of experience at the intersection of innovation, growth, and execution.
              He has co-founded and scaled startups, contributed to multiple successful exits, guided dozens of companies
              from concept to traction, and launched a university-based incubator at San Jose State University.
            </p>
            <p>
              As an Adjunct Professor at San Jose State’s College of Business, he has also taught and mentored hundreds
              of aspiring founders. His approach blends practical entrepreneurial experience with frameworks that help
              founders cut through confusion and move forward with more confidence.
            </p>
            <p>
              Foclarity advising brings that same approach into a more structured operating system, combining founder
              support, peer perspective, and software that keeps the most important work visible.
            </p>
          </div>
        </div>
      </section>

      <section class="difference-section">
        <div class="section-head compact">
          <div class="eyebrow">Why It Is Different</div>
          <h2>Not just advice. A clearer system for staying focused and following through.</h2>
        </div>
        <div class="difference-grid">
          <article class="difference-card">
            <h3>Founder context</h3>
            <p>
              The work is rooted in the real pressures founders face, not generic business theory.
            </p>
          </article>
          <article class="difference-card">
            <h3>Structure that sticks</h3>
            <p>
              Advising can connect directly into scorecards, tasks, and team alignment so the work stays alive between conversations.
            </p>
          </article>
          <article class="difference-card">
            <h3>Support beyond solo thinking</h3>
            <p>
              Foclarity can also connect you to peer groups and platform support when you need more than a single conversation.
            </p>
          </article>
        </div>
      </section>

      <section class="cta-band">
        <div class="cta-copy">
          <div class="eyebrow">Next Step</div>
          <h2>Want help getting unstuck and moving forward?</h2>
          <p>
            If you want a better thought partner, clearer priorities, and more structure around execution,
            let’s talk about whether founder advising is a fit.
          </p>
        </div>
        <div class="cta-actions">
          <a href="https://www.calendly.com/foclarity/30min" target="_blank" rel="nofollow" class="cta-link">
            <button class="cta-btn primary">Schedule a Call</button>
          </a>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
export default {
  title: 'Founder Advising for Startups and Small Business Owners | Foclarity',
  description: 'Work 1:1 with Mash Ashley to get clearer priorities, make better tradeoffs, and build stronger execution as a founder or small business owner.',
  name: 'Advising',
};
</script>

<style lang="scss" scoped>
.advising-shell {
  max-width: 1160px;
  margin: 0 auto;
  padding: 48px 24px 72px;
}

.eyebrow {
  color: #9a6b00;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  margin-bottom: 14px;
}

.advising-hero,
.advising-intro,
.fit-section,
.founder-section,
.difference-section,
.cta-band {
  margin-bottom: 28px;
}

.advising-hero {
  display: grid;
  grid-template-columns: 1.08fr 0.92fr;
  gap: 26px;
  align-items: center;
  padding: 28px 0 42px;
}

.advising-hero-panel,
.offer-card,
.fit-panel,
.founder-section,
.difference-card,
.cta-band {
  background: #fff;
  border: 1px solid #dde5ec;
  border-radius: 28px;
  box-shadow: 0 16px 36px rgba(13, 34, 56, 0.05);
}

.advising-hero-copy {
  padding: 8px 0;
}

.advising-hero-copy h1,
.section-head h2,
.fit-panel h2,
.cta-copy h2 {
  color: #10263d;
  font-size: clamp(2.4rem, 4vw, 4.2rem);
  line-height: 1.06;
  margin: 0 0 18px;
}

.hero-lead,
.section-head p,
.fit-panel p,
.cta-copy p,
.founder-copy p {
  color: #29435c;
  font-size: 1.08rem;
  line-height: 1.7;
  margin: 0 0 14px;
}

.hero-sub {
  color: #5b7084;
  line-height: 1.75;
  margin: 0;
}

.hero-actions,
.cta-actions,
.founder-links {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.hero-actions,
.cta-actions {
  margin-top: 26px;
}

.cta-link,
.founder-links a {
  text-decoration: none;
}

.cta-btn {
  min-height: 54px;
  padding: 0 24px;
  border-radius: 999px;
  border: 1px solid transparent;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: transform 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;
}

.cta-btn:hover {
  transform: translateY(-1px);
}

.cta-btn.primary {
  background: #123c5a;
  color: #fff;
  box-shadow: 0 16px 30px rgba(18, 60, 90, 0.18);
}

.cta-btn.secondary {
  background: #f5f8fb;
  border-color: #cfdbe5;
  color: #123c5a;
}

.advising-hero-panel {
  background: linear-gradient(180deg, #123c5a, #234f6e);
  border-color: #123c5a;
  padding: 22px;
  color: #fff;
}

.signal-card {
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 24px;
  padding: 26px;
  height: 100%;
  background: rgba(255, 255, 255, 0.05);
}

.signal-title {
  font-size: 1.4rem;
  font-weight: 700;
  margin-bottom: 16px;
}

.signal-card ul {
  margin: 0;
  padding-left: 20px;
  line-height: 1.8;
  color: rgba(255, 255, 255, 0.92);
}

.section-head {
  max-width: 780px;
  margin-bottom: 18px;
}

.section-head.compact h2 {
  font-size: clamp(1.8rem, 3vw, 2.7rem);
}

.offer-grid,
.difference-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;
}

.offer-card,
.difference-card,
.fit-panel {
  padding: 28px;
}

.offer-card h3,
.difference-card h3,
.founder-copy h3 {
  margin: 0 0 12px;
  color: #12304b;
  font-size: 1.3rem;
}

.offer-card p,
.difference-card p {
  margin: 0;
  color: #5b7084;
  line-height: 1.72;
}

.fit-section {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px;
}

.fit-panel.list {
  display: grid;
  gap: 12px;
  align-content: start;
}

.fit-item {
  background: #f7fafc;
  border: 1px solid #dbe6ee;
  border-radius: 18px;
  padding: 16px 18px;
  color: #24445d;
  font-weight: 600;
  line-height: 1.5;
}

.founder-section {
  padding: 30px;
}

.founder-grid {
  display: grid;
  grid-template-columns: 300px 1fr;
  gap: 28px;
  align-items: start;
}

.founder-photo-card {
  background: #f7fafc;
  border: 1px solid #dbe6ee;
  border-radius: 24px;
  padding: 24px;
}

.founder-photo {
  display: block;
  width: 100%;
  max-width: 220px;
  margin: 0 auto 18px;
  border-radius: 50%;
  border: 1px solid #d2dee8;
}

.founder-links a {
  color: #9a6b00;
  font-weight: 700;
}

.cta-band {
  display: grid;
  grid-template-columns: 1.1fr 0.9fr;
  gap: 20px;
  align-items: center;
  padding: 32px 34px;
}

.secondary-link .cta-btn {
  width: 100%;
}

@media (max-width: 980px) {
  .advising-hero,
  .fit-section,
  .founder-grid,
  .cta-band,
  .offer-grid,
  .difference-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 640px) {
  .advising-shell {
    padding: 28px 16px 56px;
  }

  .advising-hero-panel,
  .offer-card,
  .fit-panel,
  .founder-section,
  .difference-card,
  .cta-band {
    border-radius: 22px;
  }

  .offer-card,
  .fit-panel,
  .founder-section,
  .difference-card,
  .cta-band {
    padding: 24px;
  }

  .advising-hero-copy {
    padding: 0;
  }

  .cta-actions,
  .hero-actions {
    flex-direction: column;
  }

  .cta-link,
  .cta-btn {
    width: 100%;
  }
}
</style>
